import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TranslocoPipe } from '@jsverse/transloco';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzInputModule } from 'ng-zorro-antd/input';

@Component({
  selector: 'ui-platform-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrl: './autocomplete.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    NzAutocompleteModule,
    FormsModule,
    NzInputModule,
    TranslocoPipe
  ]
})
export class UiPlatformAutocompleteComponent<OptionType> implements AfterViewInit {
  @Input() inputValue?: string | OptionType;
  @Input() options: OptionType[] | null = [];
  @Input() label = '';
  @Input() optionLabel: string | ((option: OptionType) => string) = '';
  @Input() optionLabelKey?: string;
  @Input() optionValueKey?: string;
  @Input() placeholder = '';
  @Input() inputSize: 'large' | 'small' | 'default' = 'large';

  @Output() inputChanged = new EventEmitter<string>();
  @Output() optionSelected = new EventEmitter<OptionType>();
  @Output() blured = new EventEmitter<void>();
  @Output() focused = new EventEmitter<void>();

  @ViewChild('input') inputTemplateRef: ElementRef | undefined;
  @ContentChild('autocompleteOption', { static: false }) autocompleteOptionTemplateRef: TemplateRef<any> | undefined;

  ngAfterViewInit(): void {
    this.inputTemplateRef?.nativeElement.focus();
  }

  onInput({ target }: Event): void {
    this.inputChanged.emit((target as HTMLInputElement).value);
  }

  // On enter, the inputValue is set to the value of the selected option
  // not the value of user typed string
  onInputEnter() {
    this.optionSelected.emit(this.inputValue as OptionType);
  }

  getTypedLabel(option: OptionType) {
    return (option as unknown as any)[this.optionLabelKey!];
  }

  getTypedValue(option: OptionType) {
    return (option as unknown as any)[this.optionValueKey!];
  }

  // If options is an array of strings, we don't need label property,
  // so we can be sure that if option is not a string, it's an object
  getLabel(option: OptionType | string): string {
    if (typeof option === 'string') {
      return option;
    }
    return typeof this.optionLabel === 'string' ? (option as Record<string, any>)?.[this.optionLabel] : this.optionLabel(option);
  }
}
